import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AppShell } from "@/components/AppShell";
import { MaterialsTable } from "@/components/materials/MaterialsTable";
import { useMaterialsStore } from "@/store/materialsStore";

const EASE = [0.22, 1, 0.36, 1] as const;

export default function MaterialTakeoffScreen() {
  const count = useMaterialsStore((s) => s.items.length);
  const [exportState, setExportState] = useState<"idle" | "queued">("idle");

  const onExport = () => {
    setExportState("queued");
    window.setTimeout(() => setExportState("idle"), 2200);
  };

  return (
    <AppShell>
      <section data-testid="materials-screen" className="mx-auto flex min-h-screen w-full max-w-5xl flex-col gap-6 px-5 py-6 md:px-10 md:py-10">
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }} className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-col gap-1">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-cad">Quantities · Level 2</span>
            <h1 data-testid="materials-headline" className="font-display text-4xl font-bold tracking-tight text-white sm:text-5xl">Material Takeoff</h1>
          </div>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cad">{count} line items · ZAR</span>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.6, ease: EASE }}>
          <MaterialsTable />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.6, ease: EASE }} className="flex flex-col items-center gap-3 pb-4">
          <motion.button
            type="button"
            data-testid="materials-export-button"
            onClick={onExport}
            disabled={exportState === "queued"}
            whileHover={{ scale: 1.008 }}
            whileTap={{ scale: 0.99 }}
            className="group relative flex h-14 w-full items-center justify-center gap-3 overflow-hidden rounded-2xl border border-safety font-display text-base font-semibold text-safety transition-colors duration-300 hover:bg-safety/10 disabled:cursor-wait"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M12 4v11" /><path d="m7.5 10.5 4.5 4.5 4.5-4.5" /><path d="M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2" />
            </svg>
            Export CSV / PDF Report
          </motion.button>
          <AnimatePresence>
            {exportState === "queued" && (
              <motion.span
                data-testid="materials-export-status"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="font-mono text-[11px] uppercase tracking-[0.2em] text-safety"
              >
                [STATUS: EXPORT_QUEUED — REPORT ENGINE PENDING]
              </motion.span>
            )}
          </AnimatePresence>
        </motion.div>
      </section>
    </AppShell>
  );
}
